// Interjections — the short bursts that carry emotion: reactions, fillers,
// and conversational sounds. Grouped reference lists sit at the bottom.
import { renderGuideSections } from "../ui/guideSections.js";

const GROUPS = {
  "Reactions & emotion": [
    ["¡Ay!", "Ouch! / Oh no!"], ["¡Uf!", "Phew! / Ugh!"], ["¡Vaya!", "Wow / Well!"],
    ["¡Genial!", "Great!"], ["¡Qué va!", "No way!"], ["¡Madre mía!", "Oh my goodness!"],
    ["¡Caramba!", "Wow / Good grief!"], ["¡Ojalá!", "I hope so! / If only!"],
  ],
  "Agreement & encouragement": [
    ["¡Claro!", "Of course!"], ["¡Vale!", "OK! (Spain)"], ["¡Venga!", "Come on! / OK!"],
    ["¡Dale!", "Go on! (Lat. Am.)"], ["¡Eso es!", "That's it!"], ["¡Ánimo!", "Cheer up! / You can do it!"],
  ],
  "Fillers & hesitation": [
    ["Bueno…", "Well…"], ["Pues…", "Well… / So…"], ["A ver…", "Let's see…"],
    ["O sea…", "I mean…"], ["Este…", "Um… (Lat. Am.)"], ["¿Sabes?", "You know?"],
  ],
  "Greetings & politeness": [
    ["¡Hola!", "Hi!"], ["¡Oye! / ¡Oiga!", "Hey! (listen)"], ["¡Salud!", "Cheers! / Bless you!"],
    ["¡Socorro! / ¡Auxilio!", "Help!"], ["¡Ojo!", "Watch out! (lit. eye)"], ["¡Basta!", "Enough!"],
  ],
};

export function interjectionsView(app) {
  const block = (title, rows) => `
    <div class="bg-white rounded-2xl border border-sage-100 p-5">
      <h3 class="text-lg font-bold text-sage-800 mb-3">${title}</h3>
      <div class="overflow-x-auto"><table class="w-full text-sm">
        <thead><tr class="text-left text-xs uppercase tracking-wide text-sage-500 border-b border-sage-100">
          <th class="py-2 pr-3">Spanish</th><th class="py-2 px-3">English</th>
        </tr></thead>
        <tbody>
          ${rows
            .map(
              ([es, en]) => `<tr class="border-b border-sage-50 last:border-0">
            <td class="py-2 pr-3 font-medium text-sage-800">${es}</td>
            <td class="py-2 px-3 text-sage-700">${en}</td>
          </tr>`
            )
            .join("")}
        </tbody>
      </table></div>
    </div>`;

  const table = `
    <div class="grid sm:grid-cols-2 gap-4 mt-6">
      ${Object.entries(GROUPS)
        .map(([title, rows]) => block(title, rows))
        .join("")}
    </div>`;

  renderGuideSections(
    app,
    "Interjections",
    "Interjections are the short exclamations that express emotion or reaction — and the little fillers that keep a conversation flowing. They're grammatically independent, so they don't change form; you just learn them whole.",
    [
      {
        title: "1. How they work",
        body: `
          <p class="text-sage-700 mb-2">An interjection stands on its own — it isn't part of the sentence's grammar. In writing it's usually wrapped in <b>¡…!</b> with the upside-down opener:</p>
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5">
            <li><i><b>¡Ay!</b> Me he hecho daño.</i> (Ouch! I've hurt myself.)</li>
            <li><i><b>¡Vaya!</b> No lo sabía.</i> (Wow! I didn't know.)</li>
          </ul>
        `,
      },
      {
        title: "2. Fillers buy you thinking time",
        body: `
          <p class="text-sage-700 mb-2">Natural speech is full of small fillers. Sprinkling these in makes you sound far more fluent than perfect grammar alone:</p>
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5">
            <li><i><b>Bueno</b>, no sé…</i> (Well, I don't know…)</li>
            <li><i><b>Pues</b> la verdad es que…</i> (Well, the truth is…)</li>
            <li><i><b>O sea</b>, lo que quiero decir es…</i> (I mean, what I want to say is…)</li>
            <li><i><b>A ver</b>…</i> (Let's see…)</li>
          </ul>
        `,
      },
      {
        title: "3. Watch the register",
        body: `
          <p class="text-sage-700 text-sm">Many interjections are regional or informal. <i>¡Vale!</i> is everywhere in Spain; Latin America leans on <i>¡Dale!</i> and <i>¡Listo!</i>. And some common exclamations are mild swears — fine among friends, risky in formal settings. When in doubt, the neutral picks are <i>¡Claro!</i>, <i>¡Genial!</i>, and <i>¡Vaya!</i>.</p>
        `,
      },
    ],
    "",
    table
  );
  window.scrollTo({ top: 0, behavior: "smooth" });
}
